import { ALL_RESULTS, BidType } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useBidsLatestPricesPerGroupQuery } from '@/features/bidding/hooks/useBidsLatestPricesPerGroupQuery'
import { useBidsLatestPricesQuery } from '@/features/bidding/hooks/useBidsLatestPricesQuery'
import { useGroupedBidsPageQuery } from '@/features/bidding/hooks/useGroupedBidsPageQuery'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { updateExtendedBidPrices } from '@/features/bidding/utils/price/updateBidPrices'

type UseBidOfferPriceBidsWithPricesResult = {
  bidsWithPrices: ExtendedBid[]
  isLoading: boolean
}

type UseBidOfferPriceBidsWithPricesParams = {
  groupParams: GroupedBidParams
  requiresPriceByActivationGroup: boolean
}

/**
 * Hook that fetches bids with appropriate default prices enriched from historical data.
 * Supports both aggregated prices (same for all activation groups) and
 * non-aggregated prices (different per activation group) based on requirements.
 *
 * @param params - Configuration object
 * @param params.groupParams - The grouped bid parameters for filtering
 * @param params.requiresPriceByActivationGroup - Whether to use per-group pricing (true) or aggregated pricing (false)
 * @returns Object containing bids with prices and loading state
 */
export const useBidOfferPriceBidsWithPrices = ({
  groupParams,
  requiresPriceByActivationGroup,
}: UseBidOfferPriceBidsWithPricesParams): UseBidOfferPriceBidsWithPricesResult => {
  const { selectedCountry } = useBiddingContext()

  const { groupedBidsPage, isLoading: isLoadingGroupedBids } = useGroupedBidsPageQuery({
    pagination: { pageSize: ALL_RESULTS, page: 0 },
    filter: {
      items: [
        { field: 'portfolio', operator: 'equals', value: groupParams.portfolioCode },
        { field: 'marketProgram', operator: 'equals', value: groupParams.marketProgram },
        { field: 'deliveryDay', operator: 'equals', value: groupParams.deliveryDay },
        { field: 'bidType', operator: 'equals', value: groupParams.bidType },
      ],
    },
    country: selectedCountry,
  })

  // Conditionally fetch prices based on requirements
  const { prices: aggregatedPrices, isLoading: isLoadingAggregatedPrices } = useBidsLatestPricesQuery(
    {
      country: selectedCountry,
      portfolio: groupParams.portfolioCode,
      marketProgram: groupParams.marketProgram,
      bidType: groupParams.bidType,
      untilDeliveryDay: groupParams.deliveryDay.minus({ days: 1 }),
    },
    { enabled: !requiresPriceByActivationGroup },
  )

  const { prices: pricesPerGroup, isLoading: isLoadingPricesPerGroup } = useBidsLatestPricesPerGroupQuery(
    {
      country: selectedCountry,
      portfolio: groupParams.portfolioCode,
      marketProgram: groupParams.marketProgram,
      bidType: groupParams.bidType,
      untilDeliveryDay: groupParams.deliveryDay.minus({ days: 1 }),
    },
    { enabled: requiresPriceByActivationGroup },
  )

  const isLoading = isLoadingGroupedBids || isLoadingAggregatedPrices || isLoadingPricesPerGroup

  const bidsWithPrices = isLoading
    ? []
    : (groupedBidsPage?.groupedBids
        .flatMap((groupedBid) => Object.values(groupedBid.bids))
        .map((bid) => {
          if (requiresPriceByActivationGroup) {
            // Use per-group pricing
            const prices = pricesPerGroup.get(bid.activationGroupUuid) ?? []
            // For energy bids, seed only the lowest previous-day price as a single price step
            const pricesToApply =
              groupParams.bidType === BidType.ENERGY && prices.length > 0 ? [Math.min(...prices)] : prices
            return addPricesIfMissing(bid, pricesToApply)
          } else {
            // Use aggregated pricing
            return addPricesIfMissing(bid, aggregatedPrices || [])
          }
        }) ?? [])

  return {
    bidsWithPrices,
    isLoading,
  }
}

export function addPricesIfMissing(bid: ExtendedBid, prices: number[]) {
  if (bidHasPrices(bid)) {
    return bid
  }
  const updatedPrices = Object.fromEntries(prices.map((price) => [price, price]))
  return updateExtendedBidPrices(bid, updatedPrices)
}

function bidHasPrices(bid: ExtendedBid) {
  return bid.ptus.some((ptu) => (ptu.ptuChunks ? ptu.ptuChunks.length > 0 : false))
}

export default useBidOfferPriceBidsWithPrices
